// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import { useState, useCallback } from 'react';

const getThreshold = () => parseInt(localStorage.getItem('purchaseConfirmThreshold') || '500', 10);

/**
 * Hook for purchase confirmation on expensive items.
 * Threshold is configurable via Settings > Gameplay.
 */
export const usePurchaseConfirm = () => {
  const [pending, setPending] = useState(null); // { name, cost, onConfirm }

  const requestPurchase = useCallback((name, cost, onConfirm) => {
    const threshold = getThreshold();
    if (threshold > 0 && cost >= threshold) {
      setPending({ name, cost, onConfirm });
    } else {
      onConfirm();
    }
  }, []);

  const confirm = useCallback(() => {
    if (pending) {
      pending.onConfirm();
      setPending(null);
    }
  }, [pending]);

  const cancel = useCallback(() => setPending(null), []);

  return { pending, requestPurchase, confirm, cancel };
};

/**
 * Inline confirmation banner component for shops.
 * Renders a cyberpunk-styled confirmation bar.
 */
export const PurchaseConfirmBanner = ({ pending, onConfirm, onCancel }) => {
  if (!pending) return null;
  return (
    <div style={{
      background: 'rgba(255, 170, 0, 0.15)',
      border: '1px solid #ffaa00',
      borderRadius: '6px',
      padding: '10px 16px',
      margin: '8px 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      fontFamily: 'Rajdhani, sans-serif',
      fontSize: '14px',
      color: '#ffaa00'
    }}>
      <span>⚠️ Buy <strong>{pending.name}</strong> for <strong>{pending.cost} CR</strong>?</span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={onConfirm} style={{
          background: 'rgba(0, 255, 136, 0.2)', border: '1px solid #00ff88', color: '#00ff88',
          padding: '4px 12px', borderRadius: '4px', cursor: 'pointer',
          fontFamily: 'Orbitron, sans-serif', fontSize: '11px'
        }}>CONFIRM</button>
        <button onClick={onCancel} style={{
          background: 'rgba(255, 68, 68, 0.2)', border: '1px solid #ff4444', color: '#ff4444',
          padding: '4px 12px', borderRadius: '4px', cursor: 'pointer',
          fontFamily: 'Orbitron, sans-serif', fontSize: '11px'
        }}>CANCEL</button>
      </div>
    </div>
  );
};
